import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Tooltip,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Loader,
  Center,
  Select,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconCheck,
  IconX,
  IconFilter,
  IconRefresh,
} from "@tabler/icons-react";
import {
  useCollectionMethodsAdmin,
  useCreateCollectionMethod,
  useUpdateCollectionMethod,
  useToggleCollectionMethodActive,
} from "../../../hooks/admin/useCollectionMethods";
import CollectionMethodForm, {
  CollectionMethodFormValues,
} from "../../../components/admin/collection-methods/CollectionMethodForm";
import { CollectionMethod } from "../../../services/admin/collectionMethods.service";

interface CollectionMethodFilters {
  status?: "all" | "active" | "inactive";
}

const ITEMS_PER_PAGE = 20;

export default function CollectionMethodsAdminPage() {
  // Modal states
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);

  // Page state
  const [editingMethod, setEditingMethod] = useState<CollectionMethod | null>(
    null
  );
  const [methodToToggle, setMethodToToggle] = useState<CollectionMethod | null>(
    null
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<CollectionMethodFilters>({});
  const [pendingFilters, setPendingFilters] = useState<CollectionMethodFilters>(
    {}
  );
  const [page, setPage] = useState(1);

  // Data hooks
  const {
    data: methods = [],
    isLoading,
    refetch,
  } = useCollectionMethodsAdmin();
  const createMethod = useCreateCollectionMethod();
  const updateMethod = useUpdateCollectionMethod();
  const toggleActive = useToggleCollectionMethodActive();

  // Filter data based on search and filters
  const filteredMethods = useMemo(() => {
    let result = methods;

    // Apply search
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(
        (method) =>
          method.name.toLowerCase().includes(searchLower) ||
          method.description?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (filters.status === "active") {
      result = result.filter((method) => method.isActive);
    } else if (filters.status === "inactive") {
      result = result.filter((method) => !method.isActive);
    }

    return result;
  }, [methods, debouncedSearch, filters]);

  // Pagination
  const totalPages = Math.ceil(filteredMethods.length / ITEMS_PER_PAGE);
  const paginatedMethods = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredMethods.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredMethods, page]);

  // Check if any filters are active
  const hasActiveFilters = filters.status && filters.status !== "all";

  // Handlers
  const handleAdd = () => {
    setEditingMethod(null);
    openFormModal();
  };

  const handleEdit = (method: CollectionMethod) => {
    setEditingMethod(method);
    openFormModal();
  };

  const handleSubmit = async (values: CollectionMethodFormValues) => {
    if (editingMethod) {
      await updateMethod.mutateAsync({
        id: editingMethod.id,
        ...values,
      });
    } else {
      await createMethod.mutateAsync(values);
    }
    closeFormModal();
  };

  const handleToggleActive = (method: CollectionMethod) => {
    setMethodToToggle(method);
    openConfirmModal();
  };

  const confirmToggleActive = async () => {
    if (methodToToggle) {
      await toggleActive.mutateAsync({
        id: methodToToggle.id,
        isActive: !methodToToggle.isActive,
      });
      closeConfirmModal();
      setMethodToToggle(null);
    }
  };

  // Filter handlers
  const handleOpenFilterModal = () => {
    setPendingFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...pendingFilters });
    setPage(1);
    closeFilterModal();
  };

  const handleClearPendingFilters = () => {
    setPendingFilters({});
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPendingFilters({});
    setPage(1);
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Collection Methods</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage specimen collection and acquisition methods
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            {/* Search Section */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by name or description..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            {/* Filters Section */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {filters.status && filters.status !== "all" && (
                      <Badge variant="light" color="green">
                        {filters.status === "active" ? "Active" : "Inactive"}
                      </Badge>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {filteredMethods.length} method
                  {filteredMethods.length !== 1 ? "s" : ""} found
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              {/* Table Header with Pagination */}
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Collection Methods ({filteredMethods.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(page * ITEMS_PER_PAGE, filteredMethods.length)}{" "}
                      of {filteredMethods.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {/* Table */}
              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredMethods.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {hasActiveFilters || search
                    ? "No methods match the current filters"
                    : "No collection methods found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Method Name</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th style={{ width: 100 }}>Status</Table.Th>
                      <Table.Th style={{ width: 120 }}>Created</Table.Th>
                      <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedMethods.map((method) => (
                      <Table.Tr
                        key={method.id}
                        style={{
                          opacity: method.isActive ? 1 : 0.5,
                        }}
                      >
                        {/* Method Name */}
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {method.name}
                          </Text>
                        </Table.Td>

                        {/* Description */}
                        <Table.Td>
                          <Text size="sm" lineClamp={1}>
                            {method.description || "—"}
                          </Text>
                        </Table.Td>

                        {/* Status */}
                        <Table.Td>
                          <Badge
                            color={method.isActive ? "green" : "red"}
                            variant="light"
                            size="sm"
                          >
                            {method.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </Table.Td>

                        {/* Created */}
                        <Table.Td>
                          <Text size="sm">
                            {new Date(method.createdAt).toLocaleDateString()}
                          </Text>
                        </Table.Td>

                        {/* Actions */}
                        <Table.Td>
                          <Group gap="xs">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(method)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip
                              label={
                                method.isActive ? "Deactivate" : "Activate"
                              }
                            >
                              <ActionIcon
                                variant="subtle"
                                color={method.isActive ? "red" : "green"}
                                onClick={() => handleToggleActive(method)}
                              >
                                {method.isActive ? (
                                  <IconX size={18} />
                                ) : (
                                  <IconCheck size={18} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Add/Edit Method Modal */}
        <Modal
          opened={formModalOpened}
          onClose={closeFormModal}
          title={
            editingMethod
              ? "Edit Collection Method"
              : "Add New Collection Method"
          }
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <CollectionMethodForm
              initialValues={editingMethod || undefined}
              onSubmit={handleSubmit}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeFormModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createMethod.isPending || updateMethod.isPending}
                onClick={() => {
                  const form = document.querySelector("form");
                  if (form) form.requestSubmit();
                }}
              >
                {editingMethod ? "Update" : "Create"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Collection Methods"
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Select
              label="Status"
              placeholder="All statuses"
              data={[
                { value: "all", label: "All" },
                { value: "active", label: "Active Only" },
                { value: "inactive", label: "Inactive Only" },
              ]}
              value={pendingFilters.status || "all"}
              onChange={(value) =>
                setPendingFilters({
                  ...pendingFilters,
                  status:
                    (value as CollectionMethodFilters["status"]) || undefined,
                })
              }
              clearable
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={handleClearPendingFilters}>
                Clear All
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Confirmation Modal for Activate/Deactivate */}
        <Modal
          opened={confirmModalOpened}
          onClose={closeConfirmModal}
          title={
            methodToToggle?.isActive
              ? "Deactivate Collection Method"
              : "Activate Collection Method"
          }
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to{" "}
              {methodToToggle?.isActive ? "deactivate" : "activate"} the
              collection method <strong>{methodToToggle?.name}</strong>?
            </Text>
            {methodToToggle?.isActive && (
              <Text size="sm" c="dimmed">
                Deactivated methods will no longer be available for selection.
              </Text>
            )}
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeConfirmModal}>
                Cancel
              </Button>
              <Button
                color={methodToToggle?.isActive ? "red" : "green"}
                onClick={confirmToggleActive}
                loading={toggleActive.isPending}
              >
                {methodToToggle?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
